import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { CurrentUser, LoginResponse } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Card, Empty, Note, PageHead } from '../components/ui'
import { useApi } from '../hooks/useApi'

/** Digits only, the way the login box already behaves. */
function digits(value: string): string {
  return value.replace(/\D/g, '')
}

/**
 * The one screen anybody can open, whatever they are allowed to see elsewhere.
 *
 * <p>It exists because until now a PIN could not be changed at all - set once
 * when the account was made, and after that only an edit to the database would
 * move it. The lever against a lost phone was changing the signing secret,
 * which ends every session but leaves the PIN alone, so whoever had the phone
 * signed straight back in.
 *
 * <p>Reached from the name in the top bar rather than from «مزید», because
 * «مزید» is owner-only and the counter account is exactly the one whose PIN
 * needs changing.
 */
export function Account() {
  const { user, isOwner, signIn } = useAuth()

  return (
    <>
      <PageHead title="میرا کھاتہ" hint={user ? `${user.displayName} — ${user.username}` : ''} />
      <MyPin onDone={signIn} />
      {isOwner && <CounterPin />}
    </>
  )
}

/**
 * Changing your own PIN.
 *
 * <p>The old PIN is asked for and checked by the server. A screen that set a
 * new PIN on the strength of an unlocked tablet would hand the account to
 * whoever picked it up, which is the opposite of what this is for.
 */
function MyPin({ onDone }: { onDone: (username: string, pin: string) => Promise<void> }) {
  const { user } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  /* Typed twice, because there is no "forgot my PIN" on this screen and a
     mistyped new PIN locks the owner out of his own book until somebody edits
     the database. The counter account has the owner to fall back on; the owner
     has nobody. */
  const mismatch = again !== '' && next !== again
  const tooShort = next !== '' && next.length < 4

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(false)
    setBusy(true)
    try {
      const response = await api.post<LoginResponse>('/auth/pin', {
        currentPin: current,
        newPin: next,
      })
      /* The server ends every session on this account the moment the PIN
         changes - including this one. It hands back a fresh token for exactly
         that reason, and signing in again with the new PIN is the simplest way
         to put it where the rest of the app looks for it. */
      void response
      await onDone(user!.username, next)
      setDone(true)
      setCurrent('')
      setNext('')
      setAgain('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="اپنا پن بدلیں">
      {error && <Note kind="error">{error}</Note>}
      {done && (
        <Note kind="info">
          پن بدل گیا۔ جس فون پر بھی یہ کھاتہ کھلا تھا، وہ اب بند ہو گیا ہے۔
        </Note>
      )}

      <form onSubmit={submit}>
        <div className="field">
          <label>پرانا پن</label>
          <input
            type="password"
            inputMode="numeric"
            className="pin-field"
            value={current}
            maxLength={10}
            onChange={(e) => setCurrent(digits(e.target.value))}
            autoComplete="current-password"
          />
        </div>

        <div className="field">
          <label>نیا پن</label>
          <input
            type="password"
            inputMode="numeric"
            className="pin-field"
            value={next}
            maxLength={10}
            onChange={(e) => setNext(digits(e.target.value))}
            autoComplete="new-password"
          />
        </div>

        <div className="field">
          <label>نیا پن دوبارہ</label>
          <input
            type="password"
            inputMode="numeric"
            className="pin-field"
            value={again}
            maxLength={10}
            onChange={(e) => setAgain(digits(e.target.value))}
            autoComplete="new-password"
          />
        </div>

        {tooShort && <p className="muted small">پن کم از کم چار ہندسوں کا ہو۔</p>}
        {mismatch && <p className="field-error">دونوں پن ایک جیسے نہیں ہیں۔</p>}

        <p className="muted small">
          پن بدلتے ہی ہر فون سے یہ کھاتہ بند ہو جائے گا — اگر کوئی فون گم ہو گیا ہے تو یہی کرنا
          ہے۔
        </p>

        <button
          className="btn btn--save"
          disabled={busy || !current || next.length < 4 || next !== again}
        >
          {busy ? 'بدل رہے ہیں…' : 'پن بدلیں'}
        </button>
      </form>
    </Card>
  )
}

/**
 * The owner setting the counter account's PIN.
 *
 * <p>No old PIN here, and that is the whole reason it exists: it is used when
 * nobody knows the old one any more - forgotten, or gone with a phone. Owner
 * only, and the server refuses to point it at anything but a STAFF account.
 */
function CounterPin() {
  const staff = useApi<CurrentUser[]>('/auth/staff')
  const [who, setWho] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const accounts = staff.data ?? []
  const chosen = who || (accounts.length === 1 ? accounts[0].username : '')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    setBusy(true)
    try {
      await api.post('/auth/staff/pin', { username: chosen, newPin: next })
      setDone(chosen)
      setNext('')
      setAgain('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (!staff.loading && accounts.length === 0) {
    return (
      <Card title="کاؤنٹر کا پن">
        <Empty>اِس کھاتے پر کاؤنٹر کا کوئی اکاؤنٹ نہیں ہے۔</Empty>
      </Card>
    )
  }

  return (
    <Card title="کاؤنٹر کا پن">
      {error && <Note kind="error">{error}</Note>}
      {done && (
        <Note kind="info">
          «{done}» کا پن بدل دیا۔ جس فون پر وہ کھلا تھا، وہ اب بند ہے — نیا پن اُسے بتا دیں۔
        </Note>
      )}

      <p className="muted small">
        جب کاؤنٹر والا اپنا پن بھول جائے، یا فون گم ہو جائے۔ پرانا پن یہاں نہیں چاہیے۔
      </p>

      <form onSubmit={submit}>
        {accounts.length > 1 && (
          <div className="field">
            <label>کس کا</label>
            <select value={chosen} onChange={(e) => setWho(e.target.value)}>
              <option value="">— چنیں —</option>
              {accounts.map((person) => (
                <option key={person.username} value={person.username}>
                  {person.displayName} — {person.username}
                </option>
              ))}
            </select>
          </div>
        )}

        {accounts.length === 1 && (
          <p className="muted small">
            <b>{accounts[0].displayName}</b> — {accounts[0].username}
          </p>
        )}

        <div className="field">
          <label>نیا پن</label>
          <input
            type="password"
            inputMode="numeric"
            className="pin-field"
            value={next}
            maxLength={10}
            onChange={(e) => setNext(digits(e.target.value))}
            autoComplete="new-password"
          />
        </div>

        <div className="field">
          <label>نیا پن دوبارہ</label>
          <input
            type="password"
            inputMode="numeric"
            className="pin-field"
            value={again}
            maxLength={10}
            onChange={(e) => setAgain(digits(e.target.value))}
            autoComplete="new-password"
          />
        </div>

        {again !== '' && next !== again && (
          <p className="field-error">دونوں پن ایک جیسے نہیں ہیں۔</p>
        )}

        <button
          className="btn btn--save"
          disabled={busy || !chosen || next.length < 4 || next !== again}
        >
          {busy ? 'بدل رہے ہیں…' : 'کاؤنٹر کا پن بدلیں'}
        </button>
      </form>
    </Card>
  )
}
